import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { glassFocusRing } from '@aurora-pvi/design-system/glass';

/**
 * The four-minute walkthrough.
 *
 * A guided tour rather than a recording: it drives the real screens against
 * the real database, so nothing here can drift out of step with the product
 * the way a slide deck does. If a figure changes, the demo changes with it.
 *
 * The narrative is argued in docs/demo-narrative.md — question, tension,
 * evidence, trust, decision. The two decisions worth restating here:
 *
 *  - It skips the filtered product list and clicks straight into the product.
 *    A list is a transition, not a point, and the thirty seconds it would have
 *    taken belong to the scene that carries the argument.
 *
 *  - It ends where it started. Returning to the executive screen closes the
 *    loop on the question the first scene asked, which is what separates a
 *    demonstration from a tour.
 *
 * Timings are generous rather than tight — a presenter who wants to talk over
 * a scene pauses; one who does not lets it run. Every step is also reachable
 * by hand, because a demo that can only be watched cannot answer a question.
 */

export interface DemoStep {
  route: string;
  /** The scene's own claim, in the presenter's voice. */
  headline: string;
  /** What to say. Kept short — this is a prompt, not a script to read out. */
  body: string;
  /** The figures on screen worth pointing at. */
  cues: string[];
  seconds: number;
}

/** Crew Planning & Rostering — see the narrative doc for why this product. */
const DEMO_PRODUCT_ID = 'ae3dda78-fd37-54f1-bdb1-97233daec24f';

export const DEMO_STEPS: DemoStep[] = [
  {
    route: '/v2/executive',
    headline: 'Where does the money go, and what comes back?',
    body: 'Aurora spends $1.07B a year running technology and claims $2.38B of business value against it. Every figure on this screen is read from the operational database, not typed into a deck.',
    cues: ['Cost (YTD) $1.07B', 'Business value $2.38B', '36 products at risk', 'Health 59 — moderate risk'],
    seconds: 40,
  },
  {
    route: `/v2/products/${DEMO_PRODUCT_ID}`,
    headline: 'A product can be delivering and still be in trouble',
    body: 'Crew Planning & Rostering returns $22.7M — 98% of its benefit target, at 1.25 times return. And it is Critical, at 28 out of 100. A dashboard would stop at the red. This says why: risk posture 19 of 100, and a third of every change it ships fails.',
    cues: [
      'Health 28 — Critical, ring drawn in the band colour',
      'Benefit realised $22.7M — 98% of target',
      'Change failure rate 34.6%',
      'Risk posture 19 of 100 — the worst dimension',
    ],
    seconds: 60,
  },
  {
    route: '/v2/applications',
    headline: 'What the portfolio actually rests on',
    body: 'Those products sit on an estate of 1,900 applications. 1,760 map to a product. 140 do not — nobody can currently say what they are for, which is where rationalisation starts.',
    cues: ['1,900 applications', 'Mapped to a product 1,760', 'Unmapped 140', 'Business critical 855'],
    seconds: 30,
  },
  {
    route: '/v2/applications/duplicates',
    headline: '$44.7M already marked to go',
    body: 'Applications carrying a disposition of retire, replace or consolidate account for $44.7M of annual run cost. That is not a projection — it is the run cost of decisions already taken.',
    cues: ['Addressable saving $44.7M', 'Duplicate pairs share a capability'],
    seconds: 25,
  },
  {
    route: '/v2/calculations',
    headline: 'Every number has an argument behind it',
    body: 'Change failure rate — the figure from two screens ago — is failed deployments over all deployments, with the exclusions stated. Nothing is an estimate dressed as a measurement. And where a feed does not report something, the product says so rather than showing a zero it did not measure.',
    cues: [
      'Formula, denominator, exclusions, period — per metric',
      'Span of control narrows what a reader sees, never what a figure means',
    ],
    seconds: 45,
  },
  {
    route: '/v2/executive',
    headline: 'One question, answered end to end',
    body: 'From $1.07B down to a single product, the applications beneath it, and the arithmetic behind every figure — in four minutes, against live data. That is the difference between a portfolio report and a portfolio system.',
    cues: ['Back where we started, with the question answered'],
    seconds: 40,
  },
];

export const DEMO_TOTAL_SECONDS = DEMO_STEPS.reduce((sum, step) => sum + step.seconds, 0);

export function DemoTour({ onExit }: { onExit: () => void }) {
  const navigate = useNavigate();
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const step = DEMO_STEPS[index]!;

  // The route change is an effect of the step, not of the click, so stepping
  // backwards and forwards both navigate and a step reached any way at all
  // lands on the right screen.
  useEffect(() => {
    navigate(step.route);
    setElapsed(0);
  }, [index, navigate, step.route]);

  const advance = useCallback(() => {
    setIndex((current) => (current + 1 < DEMO_STEPS.length ? current + 1 : current));
  }, []);

  const timer = useRef<number | null>(null);
  useEffect(() => {
    if (paused) return;
    timer.current = window.setInterval(() => {
      setElapsed((value) => {
        if (value + 1 >= step.seconds) {
          // Last scene holds rather than looping — a demo that restarts itself
          // while somebody is answering a question is worse than one that stops.
          if (index + 1 < DEMO_STEPS.length) advance();
          return step.seconds;
        }
        return value + 1;
      });
    }, 1000);
    return () => {
      if (timer.current) window.clearInterval(timer.current);
    };
  }, [paused, step.seconds, index, advance]);

  // Escape leaves, arrows step. A presenter should never have to find a button.
  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') onExit();
      if (event.key === 'ArrowRight') advance();
      if (event.key === 'ArrowLeft') setIndex((c) => Math.max(0, c - 1));
      if (event.key === ' ') {
        event.preventDefault();
        setPaused((p) => !p);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [advance, onExit]);

  const progress = Math.min(1, elapsed / step.seconds);

  return (
    <div
      className="pointer-events-none fixed inset-x-0 bottom-0 z-[120] flex justify-center px-4 pb-4"
      role="region"
      aria-label="Guided tour"
    >
      <div
        className="pointer-events-auto w-full max-w-[860px] overflow-hidden rounded-[18px] border shadow-lg"
        style={{
          borderColor: 'var(--g-edge)',
          background: 'var(--g-surface-solid)',
          boxShadow: 'var(--g-shadow)',
        }}
      >
        {/* Time remaining in this scene, as a line rather than a number. */}
        <div className="h-[3px] w-full" style={{ background: 'var(--g-surface-2)' }}>
          <div
            className="h-full transition-[width] duration-1000 ease-linear"
            style={{ width: `${progress * 100}%`, background: 'var(--g-accent)' }}
          />
        </div>

        <div className="flex flex-col gap-3 px-5 py-4">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <span className="flex items-center gap-2">
              <span
                className="flex h-5 w-5 items-center justify-center rounded-full text-[10.5px] tabular-nums"
                style={{ background: 'var(--g-accent)', color: 'var(--g-canvas)' }}
              >
                {index + 1}
              </span>
              <span className="text-[14px] font-medium text-[var(--g-text)]">{step.headline}</span>
            </span>
            <span className="text-[11px] tabular-nums text-[var(--g-text-muted)]">
              Scene {index + 1} of {DEMO_STEPS.length}
            </span>
          </div>

          <p className="max-w-prose text-[12.5px] leading-relaxed text-[var(--g-text-secondary)]">
            {step.body}
          </p>

          <ul className="flex flex-wrap gap-1.5">
            {step.cues.map((cue) => (
              <li
                key={cue}
                className="rounded-full border px-2.5 py-1 text-[11px]"
                style={{ borderColor: 'var(--g-edge)', color: 'var(--g-text-muted)' }}
              >
                {cue}
              </li>
            ))}
          </ul>

          <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
            <span className="text-[10.5px] text-[var(--g-text-faint)]">
              Space pauses · ← → step · Esc exits
            </span>
            <span className="flex items-center gap-1.5">
              <TourButton onClick={() => setIndex((c) => Math.max(0, c - 1))} disabled={index === 0}>
                Back
              </TourButton>
              <TourButton onClick={() => setPaused((p) => !p)}>
                {paused ? 'Resume' : 'Pause'}
              </TourButton>
              {index + 1 < DEMO_STEPS.length ? (
                <TourButton primary onClick={advance}>
                  Next
                </TourButton>
              ) : (
                <TourButton primary onClick={onExit}>
                  Finish
                </TourButton>
              )}
              <TourButton onClick={onExit}>Exit</TourButton>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function TourButton({
  children,
  onClick,
  primary = false,
  disabled = false,
}: {
  children: React.ReactNode;
  onClick: () => void;
  primary?: boolean;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`rounded-full border px-3 py-1 text-[12px] transition-colors disabled:opacity-40 ${glassFocusRing}`}
      style={{
        borderColor: primary ? 'var(--g-accent)' : 'var(--g-edge)',
        background: primary ? 'var(--g-accent)' : 'transparent',
        color: primary ? 'var(--g-canvas)' : 'var(--g-text-secondary)',
      }}
    >
      {children}
    </button>
  );
}
